import type { CSSProperties } from 'react';
import type { ImageStats } from '@lumos/core';
import { defaultRecipe } from '@lumos/core';
import { useSelectedPhoto, useStore } from '../../state/store.ts';
import { Panel } from '../controls/Panel.tsx';
import { Slider } from '../controls/Slider.tsx';
import { Tip } from '../controls/Tooltip.tsx';

const DEFAULTS = defaultRecipe();

const ROW: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 6,
  margin: '4px 0 2px',
};

/** Mean the frame is aimed at: sRGB-encoded mid-grey. */
const TARGET_MEAN = 0.46;
/** Endpoint targets, kept just inside the range so nothing is crushed or blown. */
const TARGET_BLACK = 0.005;
const TARGET_WHITE = 0.985;
/** Display encode is close enough to a 2.2 power for a first-pass estimate. */
const DISPLAY_GAMMA = 2.2;

function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value));
}

/**
 * Auto tone, derived from the measured frame rather than guessed.
 *
 * Exposure: reflected-light metering rests on the assumption that an average
 * scene integrates to mid-grey. Mid-grey is 0.18 in linear light, which the
 * sRGB encode places at about 0.46. Display values track roughly the 1/2.2
 * power of linear light, so moving the measured mean from `m` to 0.46 takes
 * `2.2 * log2(0.46 / m)` stops. Brightening is then capped by the remaining
 * highlight headroom, because lifting an already-clipped frame recovers
 * nothing, and the whole move is held inside ±2 EV, the same bound the plan
 * validator puts on an AI exposure move.
 *
 * Whites and blacks: the base pass applies a levels move,
 *   d' = (d - black) / (white - black),
 *   white = 1 - (whites / 100) * 0.28,   black = -(blacks / 100) * 0.16
 * (see BASE_FRAGMENT in engine/shaders.ts). Asking that the post-exposure 1st
 * percentile land on 0.005 and the 99th on 0.985 gives two equations in
 * `white` and `black`, which solve directly to the two slider values. Contrast,
 * highlights and shadows are deliberately left alone: those are interpretation,
 * and the measurement says nothing about them.
 */
function autoToneValues(stats: ImageStats): { exposure: number; whites: number; blacks: number } {
  const mean = Math.max(stats.meanLuminance, 0.01);
  const whitePoint = clamp(stats.whitePoint, 0.01, 1);
  const blackPoint = clamp(stats.blackPoint, 0, 1);

  let exposure = DISPLAY_GAMMA * Math.log2(TARGET_MEAN / mean);
  if (exposure > 0) {
    const headroom = DISPLAY_GAMMA * Math.log2(0.995 / whitePoint);
    exposure = Math.min(exposure, Math.max(0, headroom));
  }
  exposure = clamp(exposure, -2, 2);

  const gain = 2 ** (exposure / DISPLAY_GAMMA);
  const low = clamp(blackPoint * gain, 0, 1);
  const high = clamp(whitePoint * gain, 0, 1);

  const span = Math.max(high - low, 0.02) / (TARGET_WHITE - TARGET_BLACK);
  const black = low - TARGET_BLACK * span;
  const white = black + span;

  return {
    exposure: Math.round(exposure * 100) / 100,
    blacks: Math.round(clamp((-black / 0.16) * 100, -100, 100)),
    whites: Math.round(clamp(((1 - white) / 0.28) * 100, -100, 100)),
  };
}

export function LightPanel() {
  const photo = useSelectedPhoto();
  const hasStats = useStore((state) => state.stats !== null);
  const updateRecipe = useStore((state) => state.updateRecipe);
  const commit = useStore((state) => state.commit);

  if (!photo) return null;
  const recipe = photo.recipe;

  const active =
    recipe.exposure !== DEFAULTS.exposure ||
    recipe.contrast !== DEFAULTS.contrast ||
    recipe.highlights !== DEFAULTS.highlights ||
    recipe.shadows !== DEFAULTS.shadows ||
    recipe.whites !== DEFAULTS.whites ||
    recipe.blacks !== DEFAULTS.blacks;

  const autoTone = () => {
    const stats = useStore.getState().stats;
    if (!stats) return;
    const next = autoToneValues(stats);
    updateRecipe(
      (draft) => {
        draft.exposure = next.exposure;
        draft.whites = next.whites;
        draft.blacks = next.blacks;
      },
      { commit: true },
    );
  };

  return (
    <Panel id="light" title="Light" active={active}>
      <div style={ROW}>
        <Tip
          title="Auto tone"
          body="Reads the histogram and sets exposure, whites and blacks. They are ordinary sliders after that."
        >
          <button type="button" className="btn tiny" onClick={autoTone} disabled={!hasStats}>
            Auto tone
          </button>
        </Tip>
        {!hasStats ? <span className="label">Waiting on the first render</span> : null}
      </div>

      <Slider
        label="Exposure"
        value={recipe.exposure}
        min={-5}
        max={5}
        step={0.01}
        neutral={0}
        unit=" EV"
        help="Overall brightness, in stops. Set this before anything else."
        onChange={(value) =>
          updateRecipe((draft) => {
            draft.exposure = value;
          })
        }
        onCommit={commit}
      />
      <Slider
        label="Contrast"
        value={recipe.contrast}
        min={-100}
        max={100}
        step={1}
        neutral={0}
        help="Steepens the mid-tones and leaves the endpoints alone. Set whites and blacks first."
        onChange={(value) =>
          updateRecipe((draft) => {
            draft.contrast = value;
          })
        }
        onCommit={commit}
      />
      <Slider
        label="Highlights"
        value={recipe.highlights}
        min={-100}
        max={100}
        step={1}
        neutral={0}
        help="Recovers detail in the brightest areas without moving the white point."
        onChange={(value) =>
          updateRecipe((draft) => {
            draft.highlights = value;
          })
        }
        onCommit={commit}
      />
      <Slider
        label="Shadows"
        value={recipe.shadows}
        min={-100}
        max={100}
        step={1}
        neutral={0}
        help="Opens the darkest areas without moving the black point. Past about +60 it goes flat."
        onChange={(value) =>
          updateRecipe((draft) => {
            draft.shadows = value;
          })
        }
        onCommit={commit}
      />
      <Slider
        label="Whites"
        value={recipe.whites}
        min={-100}
        max={100}
        step={1}
        neutral={0}
        help="Sets where the image reaches white. Raise it until the histogram just touches the right edge."
        onChange={(value) =>
          updateRecipe((draft) => {
            draft.whites = value;
          })
        }
        onCommit={commit}
      />
      <Slider
        label="Blacks"
        value={recipe.blacks}
        min={-100}
        max={100}
        step={1}
        neutral={0}
        help="Sets where the image reaches true black. Lowering it adds depth."
        onChange={(value) =>
          updateRecipe((draft) => {
            draft.blacks = value;
          })
        }
        onCommit={commit}
      />
    </Panel>
  );
}
